import * as React from "react";
import { CAMPAIGN_STATUS_COLORS } from "@/constants";
import { Box, Card, Chip, CircularProgress, Grid, Stack, Tooltip, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { ClockIcon, EyeIcon, MegaphoneIcon, PencilSimpleIcon, TrashIcon, UsersIcon } from "@phosphor-icons/react";
import { PhoneIcon } from "@phosphor-icons/react/dist/icons/Phone";
import { CopyIcon } from "@phosphor-icons/react/dist/ssr/Copy";
import { DownloadIcon } from "@phosphor-icons/react/dist/ssr/Download";
import { PauseIcon } from "@phosphor-icons/react/dist/ssr/Pause";
import { PlayIcon } from "@phosphor-icons/react/dist/ssr/Play";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { useNavigate } from "react-router-dom";

import { downloadFileFromResponse } from "@/utils";

import {
	Campaign,
	campaignsKeys,
	useCampaignStats,
	useDeleteCampaign,
	useToggleCampaign,
} from "@/hooks/api/use-campaigns";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { toast } from "@/components/core/toaster";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column, FilterOption, FilterType } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { StatisticCard } from "@/components/statisticCard/statistic-card";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { CreateModal } from "./createModal";
import { API_URL, BASE_URL } from "@/runtime-env";

const formatDate = (value?: string | null): string =>
	value ? new Date(value).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—";

const formatSchedule = (startAt?: string | null, endAt?: string | null): string => {
	if (!startAt && !endAt) return "—";
	if (startAt && endAt) return `${formatDate(startAt)} – ${formatDate(endAt)}`;
	return formatDate(startAt ?? endAt);
};

const CAMPAIGN_STATUS_OPTIONS = [
	{ label: "Active", value: "active" },
	{ label: "Inactive", value: "inactive" },
	{ label: "Scheduled", value: "scheduled" },
	{ label: "Completed", value: "completed" },
];

const actionIconColor = "var(--mui-palette-text-secondary)";

export function Campaigns(): React.JSX.Element {
	const [updatingCampaignId, setUpdatingCampaignId] = React.useState<string | null>(null);
	const [exportingCampaignId, setExportingCampaignId] = React.useState<string | null>(null);

	const {
		items: campaigns,
		isPending,
		totalItems,
		refetchData: fetchCampaigns,
	} = useCachedTableStateWithUrl<Campaign>({
		queryKey: campaignsKeys.lists(),
		endpoint: "/campaigns",
	});

	const { data: stats, isLoading: isStatsLoading } = useCampaignStats();

	const {
		data: selectedCampaign,
		open: isDeleteCampaignModalOpen,
		handleClose: handleDeleteCampaignModalClose,
		handleOpen: handleDeleteCampaignModalOpen,
	} = useDialog<Campaign | null>();

	const {
		data: selectedCampaignToCreate,
		open: isCreateCampaignModalOpen,
		handleClose: handleCreateCampaignModalClose,
		handleOpen: handleCreateCampaignModalOpen,
	} = useDialog<Campaign>();

	const navigate = useNavigate();

	const deleteCampaignMutation = useDeleteCampaign();
	const toggleCampaignMutation = useToggleCampaign();

	const filtersOptions: FilterOption[] = React.useMemo(
		() => [
			{
				name: "search",
				label: "Search",
				value: "",
				type: FilterType.TEXT,
			},
			{
				name: "status",
				label: "Status",
				value: [],
				type: FilterType.MULTI_SELECT,
				options: CAMPAIGN_STATUS_OPTIONS,
			},
			{
				name: "agentId",
				label: "Agent",
				value: "",
				type: FilterType.FETCH_SELECT,
				getOptionsUrl: "/agents",
				fetchLabel: "name",
				fetchValue: "id",
			},
			{
				name: "groupId",
				label: "Group",
				value: "",
				type: FilterType.FETCH_SELECT,
				getOptionsUrl: "/crm/groups",
				fetchLabel: "name",
				fetchValue: "id",
			},
			{
				name: "startAtFrom",
				label: "Start from",
				value: "",
				type: FilterType.DATE,
			},
			{
				name: "startAtTo",
				label: "Start to",
				value: "",
				type: FilterType.DATE,
			},
			{
				name: "endAtFrom",
				label: "End from",
				value: "",
				type: FilterType.DATE,
			},
			{
				name: "endAtTo",
				label: "End to",
				value: "",
				type: FilterType.DATE,
			},
		],
		[]
	);

	const handleExportCampaignCalls = async (campaignId: string) => {
		try {
			setExportingCampaignId(campaignId);

			const token = localStorage.getItem("token");
			if (!token) {
				toast.error("Authentication token not found");
				return;
			}

			const exportRedirectUrl = `${BASE_URL}/call-logs`;

			const response = await fetch(`${API_URL}/campaigns/${campaignId}/export?url=${exportRedirectUrl}`, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!response.ok) {
				throw new Error(`Export failed: ${response.statusText}`);
			}

			await downloadFileFromResponse(response);

			toast.success("Campaign calls exported successfully");
		} catch (error: any) {
			const errorMessage = error?.message || "Campaign calls export failed";
			toast.error(errorMessage);
		} finally {
			setExportingCampaignId(null);
		}
	};

	const handleDeleteCampaign = async () => {
		if (selectedCampaign) {
			try {
				await deleteCampaignMutation.mutateAsync(selectedCampaign.id);
				handleDeleteCampaignModalClose();
			} catch {
				// Error handling is done in the mutation
			}
		}
	};

	const handleToggleCampaign = async (campaign: Campaign) => {
		setUpdatingCampaignId(campaign.id);
		try {
			await toggleCampaignMutation.mutateAsync({
				id: campaign.id,
				enabled: !campaign.enabled,
			});
		} catch {
			toast.error("Failed to toggle campaign");
		} finally {
			setUpdatingCampaignId(null);
		}
	};

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "title",
					label: "Campaign Name",
					align: "left",
					sortBy: "title",
					sx: { fontWeight: "600" },
				},
				accessorKey: "title",
				getCellValue: (row: Campaign) => (
					<Tooltip title="Go to campaign" arrow>
						<Box
							tabIndex={0}
							onClick={() => navigate(`/campaigns/${row.id}/view`)}
							sx={{
								color: "inherit",
								cursor: "pointer",
								width: "max-content",
								fontWeight: 500,
								"&:hover": { textDecoration: "underline" },
							}}
						>
							{row.title}
						</Box>
					</Tooltip>
				),
			},
			{
				header: {
					id: "agentGroup",
					label: "Agent / Group",
					align: "left",
					sx: { fontWeight: "600" },
				},
				accessorKey: "agent",
				getCellValue: (row: Campaign) => (
					<Stack spacing={0.25}>
						{row.agent ? (
							<Tooltip title="View agent" arrow>
								<Typography
									variant="body2"
									onClick={() => navigate(`/agents/${row.agent?.id}`)}
									sx={{ cursor: "pointer", width: "max-content", "&:hover": { textDecoration: "underline" } }}
								>
									{row.agent.name}
								</Typography>
							</Tooltip>
						) : (
							<Typography variant="body2" color="text.disabled">
								—
							</Typography>
						)}
						{row.group ? (
							<Typography variant="caption" color="text.secondary">
								{row.group.name}
							</Typography>
						) : null}
					</Stack>
				),
			},
			{
				header: {
					id: "schedule",
					label: "Schedule",
					align: "left",
					sortBy: "startAt",
					sx: { fontWeight: "600" },
				},
				accessorKey: "startAt",
				getCellValue: (row: Campaign) => (
					<Typography variant="body2" color="text.secondary">
						{formatSchedule(row.startAt, row.endAt)}
					</Typography>
				),
			},
			{
				header: {
					id: "status",
					label: "Status",
					align: "left",
				},
				accessorKey: "status",
				getCellValue: (row: Campaign) => (
					<Chip color={CAMPAIGN_STATUS_COLORS[row.status]} variant="outlined" size="small" label={row.status} />
				),
			},
			{
				header: {
					id: "createdAt",
					label: "Created",
					align: "left",
					sortBy: "createdAt",
					sx: { fontWeight: "600" },
				},
				accessorKey: "createdAt",
				getCellValue: (row: Campaign) => (
					<Typography variant="body2" color="text.secondary">
						{formatDate(row.createdAt)}
					</Typography>
				),
			},
			{
				header: {
					id: "actions",
					label: "",
					width: 96,
					align: "right",
				},
				accessorKey: "actions",
				getCellValue: (row: Campaign) => (
					<TableActions
						isPopoverView
						actions={[
							{
								label: row.enabled ? "Pause" : "Start",
								icon:
									updatingCampaignId === row.id ? (
										<CircularProgress size={18} />
									) : row.enabled ? (
										<PauseIcon width={18} height={18} color={actionIconColor} />
									) : (
										<PlayIcon width={18} height={18} color={actionIconColor} />
									),
								onClick: () => handleToggleCampaign(row),
							},
							{
								label: "View details",
								icon: <EyeIcon width={18} height={18} color={actionIconColor} />,
								onClick: () => navigate(`/campaigns/${row.id}/view`),
							},
						]}
						popoverActions={[
							{
								label: "Export calls",
								icon:
									exportingCampaignId === row.id ? (
										<CircularProgress size={18} />
									) : (
										<DownloadIcon width={18} height={18} color={actionIconColor} />
									),
								onClick: () => handleExportCampaignCalls(row.id),
								disabled: exportingCampaignId === row.id,
							},
							{
								label: "Edit",
								icon: <PencilSimpleIcon width={18} height={18} color={actionIconColor} />,
								onClick: () => navigate(`/campaigns/${row.id}`),
							},
							{
								label: "Copy",
								icon: <CopyIcon width={18} height={18} color={actionIconColor} />,
								onClick: () => handleCreateCampaignModalOpen(row),
							},
							{
								label: "Delete",
								icon: <TrashIcon width={18} height={18} />,
								sx: { color: "error.main" },
								onClick: () => handleDeleteCampaignModalOpen(row),
							},
						]}
					/>
				),
			},
		];
	}, [exportingCampaignId, navigate, updatingCampaignId]);

	const statisticCards = React.useMemo(() => {
		const totalCalls = stats?.totalCalls || 0;
		const outboundCalls = stats?.totalOutboundCalls || 0;
		const inboundCalls = totalCalls - outboundCalls;

		return [
			{
				title: "Total Campaigns",
				value: stats?.totalCampaigns,
				icon: <MegaphoneIcon color="var(--mui-palette-primary-main)" width={20} height={20} />,
				additonalInfo: `${stats?.activeCampaigns} active`,
			},
			{
				title: "Total Contacts",
				value: stats?.totalLeads,
				icon: <UsersIcon color="var(--mui-palette-success-main)" width={20} height={20} />,
				additonalInfo: "Across all campaigns",
			},
			{
				title: "Total Calls Made",
				value: totalCalls,
				icon: <PhoneIcon color="var(--mui-palette-info-main)" width={20} height={20} />,
				additonalInfo:
					totalCalls > 0 ? (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0, mt: { sm: -1 } }}>
							<Typography color="text.secondary" fontSize={12}>
								{inboundCalls} inbound calls
							</Typography>
							<Typography color="text.secondary" fontSize={12} lineHeight={0.5}>
								{outboundCalls} outbound calls
							</Typography>
						</Box>
					) : null,
				valueStyle: { mt: { sm: totalCalls > 0 ? -1 : 0 } },
			},
			{
				title: "Total AI Minutes",
				value: stats?.totalMinutes,
				icon: <ClockIcon color="var(--mui-palette-warning-main)" width={20} height={20} />,
				additonalInfo: `${stats?.totalHours} hours`,
			},
		];
	}, [stats]);

	const animationDelay = 150;

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Campaigns Management</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Monitor and manage your call campaigns and performance
							</Typography>
						</Box>
						<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
							<Button variant="outlined" onClick={() => navigate("/campaign-briefs/new")}>
								New campaign request
							</Button>
							<Button startIcon={<PlusIcon />} variant="contained" onClick={() => handleCreateCampaignModalOpen()}>
								Create Campaign
							</Button>
						</Box>
					</Stack>

					<Grid container spacing={3}>
						{statisticCards.map((card, index) => (
							<Grid
								key={index}
								size={{
									md: 3,
									sm: 6,
									xs: 12,
								}}
							>
								<StatisticCard
									{...card}
									isLoading={isStatsLoading}
									animationDelay={index * animationDelay}
									timeout={animationDelay * statisticCards.length}
									isLast={index === statisticCards.length - 1}
								/>
							</Grid>
						))}
					</Grid>

					<TableAnimation delay={450}>
						<Card>
							<CustomTable
								columns={columns}
								data={campaigns}
								refetchData={fetchCampaigns}
								isLoading={isPending}
								totalItems={totalItems}
								filtersOptions={filtersOptions}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<DeleteModal
				open={isDeleteCampaignModalOpen}
				onClose={handleDeleteCampaignModalClose}
				onDelete={handleDeleteCampaign}
				title="Delete Campaign"
				description="Are you sure you want to delete this campaign?"
			/>

			<CreateModal
				open={isCreateCampaignModalOpen}
				campaignToCopy={selectedCampaignToCreate}
				onClose={handleCreateCampaignModalClose}
			/>
		</React.Fragment>
	);
}
